import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faUserCircle, 
  faTrashAlt, 
  faExclamationTriangle,
  faRightFromBracket
} from "@fortawesome/free-solid-svg-icons";
import { baseURL } from "../config";
import setUpAxios from "../setUpAxios";
import { useLoginContext } from "../Contexts/LoginContext";

function UserInfoSection() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const menuRef = useRef(null);
  const iconRef = useRef(null);
  const navigate = useNavigate();
  const {username,mobileNumber,profilePicture,role,logout}=useLoginContext();

  const handleClickOutside = (event) => {
    if (
      menuRef.current && 
      !menuRef.current.contains(event.target) && 
      !iconRef.current.contains(event.target)
    ) {
      setIsMenuOpen(false);
    }
  };


  const handleDeleteAccount = async () => {
    try {
      setUpAxios();
      await axios.delete(`${baseURL}/user/delete`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`
        }
      });

      // Clear user data and logout
      localStorage.removeItem("token");
      localStorage.setItem("loggedIn", "false");
      navigate("/login");
      window.location.reload();
    } catch (error) {
      console.error("Account deletion failed:", error);
      alert("Account deletion failed. Please try again.");
    }
  };

  const handleLogout = async () => {
    try {
      logout();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <div
        ref={iconRef}
        className="flex justify-center items-center cursor-pointer hover:opacity-80 transition-opacity"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
      >
        {profilePicture ? (
          <img
            src={profilePicture}
            alt={username}
            className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 shadow-sm"
          />
        ) : (
          <div className="relative">
            <FontAwesomeIcon
              icon={faUserCircle}
              className="text-gray-600 text-3xl"
            />
            {isMenuOpen && (
              <div className="absolute inset-0 border-2 border-indigo-500 rounded-full"></div>
            )}
          </div>
        )}
      </div>

      {isMenuOpen && (
        <div
          ref={menuRef}
          className="absolute -right-5  mb-3 w-64 z-20 bg-white rounded-xl shadow-lg border border-gray-200 transform origin-bottom-right transition-all duration-200 scale-95 animate-fade-in-up"
          style={{
            boxShadow:
              "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
          }}
        >
          {/* Menu pointer */}
          <div className="absolute -bottom-1.5 right-[50%] w-4 h-4 bg-white transform rotate-45 border-r border-b border-gray-200"></div>

          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {profilePicture ? (
                  <img
                    src={profilePicture}
                    alt={username}
                    className="w-12 h-12 rounded-full border-2 border-gray-100 shadow-sm"
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faUserCircle}
                    className="text-gray-500 text-4xl"
                  />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {username}
                </p>
                <p className="text-xs text-gray-800 truncate">
                  {mobileNumber}
                </p>
                <span className="mt-1 inline-block px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
                  {role}
                </span>
              </div>
            </div>
          </div>

          <div className="p-2">
            <nav className="space-y-1">
              {/* Delete Account Option */}
              <MenuButton
                onClick={() => setShowDeleteConfirmation(true)}
                className="text-red-600 hover:bg-red-50 group"
              >
                <span className="flex items-center">
                  <FontAwesomeIcon
                    icon={faTrashAlt}
                    className="mr-2.5 text-red-500 group-hover:text-red-600 transition-colors"
                  />
                  Delete Account
                </span>
              </MenuButton>

              <MenuButton
                onClick={handleLogout}
                className="text-gray-700 hover:bg-gray-50 group"
              >
                <span className="flex items-center">
                  <FontAwesomeIcon
                    icon={faRightFromBracket}
                    className="mr-2.5 text-gray-500 group-hover:text-gray-700 transition-colors"
                  />
                  Logout
                </span>
              </MenuButton>
            </nav>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl border border-gray-100 transform transition-all duration-200 scale-95 animate-fade-in">
            <div className="text-center mb-4">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-3">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-red-500 text-xl"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                Confirm Account Deletion
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                Are you sure you want to permanently delete your account? This
                action cannot be undone and all your data will be lost.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirmation(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors shadow-md"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const MenuButton = ({ children, className, ...props }) => (
  <button
    {...props}
    className={`w-full text-left px-3 py-2.5 text-sm rounded-lg transition-colors flex items-center ${className || 'text-gray-700 hover:bg-gray-100'}`}
  >
    {children}
  </button>
);

export default UserInfoSection;